import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { PIMSDisplay, PIMSType } from "@/common/utils/types";
import { apiFetch } from "@common/lib/api";
import { HAPPY_SOAP } from "@/common/utils/constants";
import SetupLeftPanel from "./SetupLeftPanel";
import NameStep from "./steps/NameStep";
import ClinicNameStep from "./steps/ClinicNameStep";
import ClinicConfirmStep from "./steps/ClinicConfirmStep";
import ClinicPickStep from "./steps/ClinicPickStep";
import ClinicManualStep from "./steps/ClinicManualStep";
import RoleStep from "./steps/RoleStep";
import PimsSelectionStep from "./steps/PimsSelectionStep";
import MicPermissionStep from "./steps/MicPermissionStep";
import { searchClinics } from "./clinic-lookup";
import type { ClinicResult, RoleValue, SetupContext, SetupStepId } from "./setup-flow-config";
import { trackPLGEvent } from "@common/lib/plgTracking";
import { isIntercomLauncherHidden } from "@common/lib/Intercom";

const BACK_MAP: Partial<Record<SetupStepId, SetupStepId>> = {
    role: "name",
    "clinic-name": "role",
    "clinic-confirm": "clinic-name",
    "clinic-pick": "clinic-name",
    "clinic-manual": "clinic-name",
    pims: "clinic-name",
    mic: "pims",
};

interface Props {
    onComplete: () => void;
}

export default function SetupFlow({ onComplete }: Props) {
    const { handlePimsTypeChange } = useAccountContext();

    const [step, setStep] = useState<SetupStepId>("name");
    const [animState, setAnimState] = useState<"visible" | "exiting">("visible");
    const [direction, setDirection] = useState<"forward" | "back">("forward");
    const [isSearching, setIsSearching] = useState(false);
    const savingRef = useRef(false);
    const stepContentRef = useRef<HTMLDivElement>(null);

    // Hide Intercom during setup
    useEffect(() => {
        try {
            (window as any)?.Intercom?.("update", { hide_default_launcher: true });
        } catch (err) {
            console.warn("Failed to hide Intercom launcher:", err);
        }
        return () => {
            try {
                (window as any)?.Intercom?.("update", { hide_default_launcher: isIntercomLauncherHidden() });
            } catch (err) {
                console.warn("Failed to restore Intercom launcher:", err);
            }
        };
    }, []);

    // Check for reduced motion preference
    const [reducedMotion, setReducedMotion] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        setReducedMotion(mq.matches);
        const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    // Track onboarding started on mount (localStorage so it deduplicates across tabs)
    useEffect(() => {
        if (localStorage.getItem("plg_onboarding_started_fired")) return;
        localStorage.setItem("plg_onboarding_started_fired", "true");
        trackPLGEvent("onboarding_started");
    }, []);

    const transitionToStep = (nextStep: SetupStepId, goingBack = false, skipTracking = false) => {
        if (!goingBack && !skipTracking) {
            trackPLGEvent("onboarding_step_completed", { step });
        }
        if (reducedMotion) {
            setStep(nextStep);
            return;
        }
        setDirection(goingBack ? "back" : "forward");
        setAnimState("exiting");
        setTimeout(() => {
            setStep(nextStep);
            setAnimState("visible");
        }, 280);
    };

    // Stagger-animate children when a new step mounts
    useEffect(() => {
        if (animState !== "visible" || reducedMotion) return;
        const wrapper = stepContentRef.current;
        if (!wrapper) return;

        // Step components render a single root element — stagger its children
        const stepRoot = wrapper.firstElementChild as HTMLElement | null;
        if (!stepRoot) return;

        const children = Array.from(stepRoot.children) as HTMLElement[];
        if (children.length === 0) return;

        const enterFrom = direction === "forward" ? "40px" : "-40px";

        children.forEach((child) => {
            child.style.opacity = "0";
            child.style.transform = `translateX(${enterFrom})`;
            child.style.transition = "none";
        });

        // Force reflow then stagger in
        stepRoot.offsetHeight;
        const timers: ReturnType<typeof setTimeout>[] = [];
        children.forEach((child, i) => {
            timers.push(
                setTimeout(() => {
                    child.style.transition = "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s ease";
                    child.style.transform = "translateX(0)";
                    child.style.opacity = "1";
                }, i * 70),
            );
        });
        return () => timers.forEach(clearTimeout);
    }, [step, animState, direction, reducedMotion]);

    // Accumulated context
    const [context, setContext] = useState<SetupContext>({
        name: "",
        role: null,
        clinicName: "",
        clinicResult: null,
        selectedPims: null,
    });

    // Clinic search results (for pick step)
    const [clinicResults, setClinicResults] = useState<ClinicResult[]>([]);

    // --- Handlers ---

    const handleName = (name: string) => {
        setContext((prev) => ({ ...prev, name }));
        transitionToStep("role");
    };

    const handleRole = (role: RoleValue) => {
        setContext((prev) => ({ ...prev, role }));
        transitionToStep("clinic-name");
    };

    const handleClinicSearch = async (clinicName: string) => {
        setContext((prev) => ({ ...prev, clinicName }));
        setIsSearching(true);

        try {
            const results = await searchClinics(clinicName);
            setClinicResults(results);

            if (results.length === 1) {
                transitionToStep("clinic-confirm");
            } else if (results.length >= 2) {
                transitionToStep("clinic-pick");
            } else {
                transitionToStep("clinic-manual");
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.warning("Clinic search is temporarily unavailable. Please enter your details manually.");
            transitionToStep("clinic-manual");
        } finally {
            setIsSearching(false);
        }
    };

    const handleClinicPick = (clinic: ClinicResult) => {
        setClinicResults([clinic]);
        transitionToStep("clinic-confirm");
    };

    const handleClinicConfirm = () => {
        const clinic = clinicResults[0];
        if (!clinic) return;
        setContext((prev) => ({ ...prev, clinicResult: clinic }));
        transitionToStep("pims");
    };

    const handleClinicManualSubmit = (result: ClinicResult) => {
        setContext((prev) => ({ ...prev, clinicResult: result }));
        transitionToStep("pims");
    };

    const handlePimsSelect = async (pimsValue: string) => {
        if (savingRef.current) return;
        savingRef.current = true;
        try {
            setContext((prev) => ({ ...prev, selectedPims: pimsValue as PIMSType | "none" }));

            // Trigger PIMS change via AccountProvider (writes to DB and updates React state).
            // Note: profile-completed also saves current_pims — this call is kept for the
            // AccountProvider side effects (e.g., markdown_copy_enabled meta_data update).
            // changePIMS uses raw fetch which can throw on network errors, so we catch
            // to ensure the user always advances past the PIMS step.
            if (pimsValue !== "none") {
                try {
                    await handlePimsTypeChange(PIMSDisplay[pimsValue as PIMSType]);
                } catch (pimsError) {
                    Sentry.captureException(pimsError);
                    toast.warning("We couldn't save your PIMS selection. You can update it later in Settings.");
                }
            }

            transitionToStep("mic");
        } finally {
            savingRef.current = false;
        }
    };

    const handleComplete = useCallback(async () => {
        if (savingRef.current) return;
        savingRef.current = true;

        const heardAboutUs = localStorage.getItem("plg_heard_about_us");

        try {
            const body = await apiFetch<{ message: string; failedFields?: string[] }>("onboarding/profile-completed", {
                method: "POST",
                json: {
                    doctorName: context.name || undefined,
                    role: context.role || undefined,
                    clinicName: context.clinicResult?.name || undefined,
                    clinicLocation: context.clinicResult?.formattedAddress || undefined,
                    clinicPhone: context.clinicResult?.phone || undefined,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    address: context.clinicResult
                        ? {
                              street: context.clinicResult.street,
                              city: context.clinicResult.city,
                              state: context.clinicResult.state,
                              zip: context.clinicResult.zip,
                              country: context.clinicResult.country,
                          }
                        : undefined,
                    pims: context.selectedPims && context.selectedPims !== "none" ? context.selectedPims : undefined,
                    heardAboutUs: heardAboutUs || undefined,
                },
            });

            if (body?.failedFields && body.failedFields.length > 0) {
                toast.warning("Setup completed, but some details couldn't be saved. You can update them in Settings.");
            }
        } catch {
            // apiFetch already captures to Sentry — don't double-capture
            toast.error("Something went wrong finishing setup. Please try again.");
            return;
        } finally {
            savingRef.current = false;
        }

        // Only write localStorage after successful API call
        localStorage.setItem("selectedDocuments", JSON.stringify([HAPPY_SOAP]));
        localStorage.removeItem("plg_heard_about_us");

        if (!localStorage.getItem("plg_onboarding_completed_fired")) {
            localStorage.setItem("plg_onboarding_completed_fired", "true");
            trackPLGEvent("onboarding_completed", {
                role: context.role || null,
                pims: context.selectedPims || null,
            });
        }

        onComplete();
    }, [context, onComplete]);

    const handleBack = () => {
        const prev = BACK_MAP[step];
        if (prev) transitionToStep(prev, true);
    };

    // --- Render ---

    const renderStep = () => {
        switch (step) {
            case "name":
                return <NameStep onNext={handleName} defaultValue={context.name} />;
            case "role":
                return <RoleStep onSelect={handleRole} value={context.role} />;
            case "clinic-name":
                return (
                    <ClinicNameStep
                        onSearch={handleClinicSearch}
                        isSearching={isSearching}
                        defaultValue={context.clinicName}
                    />
                );
            case "clinic-confirm":
                return (
                    <ClinicConfirmStep
                        clinic={clinicResults[0]}
                        onConfirm={handleClinicConfirm}
                        onReject={() => transitionToStep("clinic-manual", false, true)}
                    />
                );
            case "clinic-pick":
                return (
                    <ClinicPickStep
                        clinics={clinicResults}
                        onSelect={handleClinicPick}
                        onNoneMatch={() => transitionToStep("clinic-manual", false, true)}
                    />
                );
            case "clinic-manual":
                return <ClinicManualStep clinicName={context.clinicName} onSubmit={handleClinicManualSubmit} />;
            case "pims":
                return <PimsSelectionStep onSelect={handlePimsSelect} />;
            case "mic":
                return <MicPermissionStep onComplete={handleComplete} onSkip={handleComplete} />;
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex bg-slate-50">
            {/* Left panel */}
            <SetupLeftPanel step={step} context={context} />

            {/* Right panel */}
            <div className="relative flex flex-1 flex-col items-center overflow-y-auto px-8 py-8 md:px-16">
                {/* Header row: back button + logo */}
                <div className="flex w-full items-center justify-between">
                    {step !== "name" ? (
                        <button
                            onClick={handleBack}
                            className="flex items-center gap-1 text-sm font-medium text-shadow-300 hover:text-shadow-500 rounded-md focused"
                        >
                            <svg
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                    ) : (
                        <div />
                    )}
                    <img src="/HD-Horizontal-Blue.png" alt="HappyDoc" className="h-8" />
                </div>

                {/* Step content */}
                <div className="flex flex-1 items-center">
                    <div
                        className={`w-full transition-[transform,opacity] duration-[280ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
                            animState === "exiting"
                                ? direction === "forward"
                                    ? "-translate-x-12 opacity-0"
                                    : "translate-x-12 opacity-0"
                                : "translate-x-0"
                        } ${reducedMotion ? "!transition-none" : ""}`}
                    >
                        <div ref={stepContentRef}>{renderStep()}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
